import { useState } from 'react';
import { X, FileText, Image, Download, ExternalLink, Loader2, File } from 'lucide-react';
import type { DealWithFiles, AttachmentFile } from '../../types/contracts';
import { contractsApi } from '../../services/contracts-api';

interface Props {
  data: DealWithFiles;
  isDark: boolean;
  onClose: () => void;
}

function formatAmount(amount: number): string {
  return amount.toLocaleString('en-US');
}

function getFileIcon(file: AttachmentFile) {
  const ext = file.extension.toLowerCase();
  if (ext === 'pdf') return <FileText className="w-5 h-5 text-red-400" />;
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return <Image className="w-5 h-5 text-blue-400" />;
  return <File className="w-5 h-5 text-gray-400" />;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export default function ContractDetailModal({ data, isDark, onClose }: Props) {
  const { deal, files } = data;
  const [viewingFile, setViewingFile] = useState<{ url: string; name: string } | null>(null);
  const [loadingFileId, setLoadingFileId] = useState<string | null>(null);

  const handleViewFile = async (file: AttachmentFile) => {
    setLoadingFileId(file.id);
    try {
      const url = await contractsApi.getFileSignedUrl(file.id);
      setViewingFile({ url, name: file.name });
    } catch (err) {
      console.error('Failed to get file URL:', err);
    } finally {
      setLoadingFileId(null);
    }
  };

  const handleDownload = async (file: AttachmentFile) => {
    setLoadingFileId(file.id);
    try {
      const url = await contractsApi.getFileSignedUrl(file.id);
      window.open(url, '_blank');
    } catch (err) {
      console.error('Failed to get file URL:', err);
    } finally {
      setLoadingFileId(null);
    }
  };

  const chungTuList = deal.kiem_tra_chung_tu.split(';').filter(Boolean);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className={`relative w-full max-w-4xl max-h-[90vh] rounded-xl shadow-2xl flex flex-col ${isDark ? 'bg-slate-900 border border-white/10' : 'bg-white border border-gray-200'}`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
          <div>
            <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {deal.dealname}
            </h2>
            <p className={`text-sm mt-0.5 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              {deal.deal_by_studio} &middot; {deal.dealtype} &middot; {formatAmount(deal.amount)} VND
            </p>
          </div>
          <button onClick={onClose} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-white/60' : 'hover:bg-gray-100 text-gray-400'}`}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Deal Info */}
          <div className={`grid grid-cols-2 sm:grid-cols-3 gap-4 p-4 rounded-lg ${isDark ? 'bg-white/[0.03]' : 'bg-gray-50'}`}>
            <div>
              <div className={`text-xs uppercase tracking-wider mb-1 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>SĐT</div>
              <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{deal.deal_phone || '-'}</div>
            </div>
            <div>
              <div className={`text-xs uppercase tracking-wider mb-1 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Sản phẩm</div>
              <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{deal.kind_of_product || '-'}</div>
            </div>
            <div>
              <div className={`text-xs uppercase tracking-wider mb-1 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Thời gian</div>
              <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{deal.deal_time || '-'}</div>
            </div>
            <div>
              <div className={`text-xs uppercase tracking-wider mb-1 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Ngày close</div>
              <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{deal.closedate ? new Date(deal.closedate).toLocaleDateString('vi-VN') : '-'}</div>
            </div>
            <div className="col-span-2">
              <div className={`text-xs uppercase tracking-wider mb-1 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Chứng từ</div>
              <div className="flex flex-wrap gap-1.5">
                {chungTuList.map((ct) => (
                  <span key={ct} className={`px-2 py-0.5 text-xs rounded-full ${isDark ? 'bg-cyan-500/15 text-cyan-300' : 'bg-cyan-50 text-cyan-700'}`}>
                    {ct}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Files */}
          <div>
            <h3 className={`text-sm font-semibold uppercase tracking-wider mb-3 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
              File đính kèm ({files.length})
            </h3>

            {files.length === 0 ? (
              <p className={`text-sm ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Không có file đính kèm</p>
            ) : (
              <div className="space-y-2">
                {files.map((file) => (
                  <div
                    key={file.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${isDark ? 'bg-white/[0.02] border-white/[0.06] hover:bg-white/[0.05]' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
                  >
                    {getFileIcon(file)}
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {file.name}
                      </div>
                      <div className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                        {file.extension.toUpperCase()} &middot; {formatFileSize(file.size)}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {file.extension.toLowerCase() === 'pdf' && (
                        <button
                          onClick={() => handleViewFile(file)}
                          disabled={loadingFileId === file.id}
                          className={`p-2 rounded-lg text-sm transition-colors ${isDark ? 'hover:bg-white/10 text-cyan-400' : 'hover:bg-cyan-50 text-cyan-600'}`}
                          title="Xem file"
                        >
                          {loadingFileId === file.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                        </button>
                      )}
                      <button
                        onClick={() => handleDownload(file)}
                        disabled={loadingFileId === file.id}
                        className={`p-2 rounded-lg text-sm transition-colors ${isDark ? 'hover:bg-white/10 text-white/60' : 'hover:bg-gray-100 text-gray-600'}`}
                        title="Tải xuống"
                      >
                        {loadingFileId === file.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* PDF Viewer */}
          {viewingFile && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className={`text-sm font-semibold uppercase tracking-wider ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                  Xem: {viewingFile.name}
                </h3>
                <button
                  onClick={() => setViewingFile(null)}
                  className={`text-xs px-2 py-1 rounded ${isDark ? 'text-white/40 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  Đóng
                </button>
              </div>
              <div className={`rounded-lg overflow-hidden border ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                <iframe
                  src={viewingFile.url}
                  className="w-full"
                  style={{ height: '500px' }}
                  title={viewingFile.name}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
